<template>
    <v-navigation-drawer
    class="side_menu_panel pa-0"
    rail
    width="325"
    elevation="0"
    rail-width="58"
    location="left"
    z-index="100000"
    close-delay="50"
    expand-on-hover
    permanent
    >
    <!-- Skeleton Loader shown while loading -->
    <div v-if="props.loading">
        <SkeletonLoader
        color="transparent"
        :type="'list-item-avatar'"
        :repeat="7"
        />
    </div>
    <!-- Ends -->
    
    <!-- Render side Menus Here -->
    <v-expansion-panels
    v-else
    v-model="panelIndex"
    class="ma-0 pa-0 side_menu_expansions"
    elevation="0"
    multiple
    >
    <v-list class="w-100" height="64px">
        <v-list-item
        class="side_menu_logo"
        :ripple="false"
        density="compact"
        :prepend-avatar="getResourceUrl(`/cdn/${sportTitle}/site/logo-icon.svg`)"
        :to="'/'"
        >
        <template v-slot:title>
            <img
            class="mt-2"
            style="height: 30px;"
            :src="getResourceUrl(`/cdn/${sportTitle}/site/logo-word.svg`)"
            :alt="`${sportTitle}Levels`"
            />
        </template>
    </v-list-item>
</v-list>

<v-expansion-panel
v-for="(panel, i) in menuPanels"
:key="i"
class="rounded-0 ma-0"
color="primary"
:readonly="i === 0"
:hide-actions="panel.hideActions"
>
<v-expansion-panel-title
v-if="panel.title"
class="ma-0 pa-5 rounded-0"
static
>
<v-list-item
id="menu_panel_title lang_translate"
class="ma-0 pa-0"
:title="panel.title"
variant="text"
style="color: rgb(var(--v-theme-secondary))"
/>
<v-icon v-if="panel.closePanel" icon="mdi-close" class="ms-auto" />
</v-expansion-panel-title>

<v-expansion-panel-text
class="ma-0m pa-0"
:style="`border-width: 0 0 0 ${panel.borderLeft || '0px'} !important`"
>
<v-list density="comfortable" nav class="ma-0 pa-0">
    <v-list-item
    v-for="(item, j) in panel.items"
    :key="j"
    class="menu_panel_item lang_translate"
    :to="item.to"
    :href="item.href"
    :target="item.target"
    :title="item.title"
    :prepend-avatar="getResourceUrl('/' + item.img)"
    :append-icon="item.appendIcon"
    @click="item.click?.()"
    />
</v-list>
</v-expansion-panel-text>
</v-expansion-panel>
</v-expansion-panels>
<!-- Ends -->

<!-- Action Buttons & NBG Section -->
<template #append>
    <SkeletonLoader
    v-if="props.loading"
    color="transparent"
    :type="'list-item-avatar'"
    :repeat="1"
    />
    
    <div v-else class="px-4 py-2 d-flex flex-column ga-3" style="border-top: 1px solid rgb(var(--v-theme-primary-800))">
        <v-list v-if="props.isLoggedIn" density="comfortable" nav class="ma-0 pa-0">
            <v-list-item
            v-if="ngbItem && ngbItem.href"
            class="menu_panel_item-ngb pa-0 ma-0 lang_translate"
            :prepend-avatar="getResourceUrl('/' + ngbItem.img.replace(/^\/+/, ''))"
            :href="ngbItem.href"
            target="_blank"
            :title="String(ngbItem.text).toUpperCase()"
            append-icon="mdi-open-in-new"
            base-color="white"
            variant="plain"
            />
        </v-list>
        <v-list v-if="props.isLoggedOut" density="comfortable" nav class="d-flex flex-column ma-0 pa-0 ga-2">
            <v-list-item class="menu_panel_item-ngb pa-0 ma-0 ga-3 lang_translate" :prepend-avatar="getResourceUrl(`/cdn/icons/login-white.svg`)" base-color="white" variant="plain">
                <Button v-if="props.isLoggedOut" :slim="true" version="primary--800" label="Login" style="width: 100%;" @click="router.push('/menu_login?action=logout')"/>
            </v-list-item> 
            <v-list-item class="menu_panel_item-ngb pa-0 ma-0 ga-3 lang_translate" :prepend-avatar="getResourceUrl(`/maya/icons/icon-player-white.svg`)" base-color="white" variant="plain">
                <Button v-if="props.isLoggedOut" :slim="true" version="action" label="Create Account" style="width: 100%;" @click="router.push('/register')"/>
            </v-list-item> 
        </v-list>
    </div>
</template>
<!-- Ends -->
</v-navigation-drawer>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { getResourceUrl, sportTitle } from '@/services/admin_actions/apiUtils'

import Button from '@/components/ComponentLibrary/Button.vue'
import SkeletonLoader from '@/components/ComponentLibrary/SkeletonLoader.vue'

const router = useRouter()

// Props
const props = defineProps<{
    loading: boolean
    leftMenuItems: Record<string, any>
    memberData: Record<string, any>
    myRankingsData: Record<string, any>
    isLoggedIn: boolean
    isLoggedOut: boolean
    ngb?: {
        img?: string
        text?: string
        href?: string
    }
}>()

// Types
interface PanelItem {
    title: string
    to?: string
    href?: string
    target?: string
    img?: string
    appendIcon?: string
    click?: () => void
}

interface MenuPanel {
    title: string
    icon?: string
    closePanel?: boolean
    hideActions?: boolean
    items: PanelItem[]
    borderLeft?: string
}

// Computed side menu panels
const menuPanels = computed<MenuPanel[]>(() => {
    const panels: MenuPanel[] = []
    
    // Popular In App (left_menu_items)
    const leftMenu = props.leftMenuItems || {}
    const leftItems: PanelItem[] = Object.values(leftMenu).map((item: any) => ({
        title: item.menu_item || item.title,
        href: item.href?.startsWith('http') ? item.href : undefined,
        to: item.href && !item.href.startsWith('http') ? `/${item.href}` : undefined,
        target: item.href?.startsWith('http') ? '_blank' : undefined,
        img: item.img || 'maya/icons/icon-settings-cog-white.svg',
        appendIcon: item.href?.startsWith('http') ? 'mdi-open-in-new' : undefined,
    }))
    panels.push({ title: '', items: leftItems, closePanel: false, hideActions: true })
    
    // Members Area Section - Unhide to show Members Area
    // const membersHref = props.memberData?.site_map?.['Members area']?.href || {}
    // const membersItems: PanelItem[] = Object.entries(membersHref).map(([key, val]: any) => ({
    //   title: key,
    //   href: val.href?.startsWith('http') ? val.href : undefined,
    //   to: val.href && !val.href.startsWith('http') ? `/${val.href}` : undefined,
    //   target: val.href?.startsWith('http') ? '_blank' : undefined,
    //   appendIcon: val.href?.startsWith('http') ? 'mdi-open-in-new' : undefined,
    // }))
    // panels.push({ title: 'Members Area', items: membersItems, borderLeft: '8px' })
    
    // My Rankings Section - Unhide to show My Rankings
    // const rankingsHref = props.myRankingsData?.site_map?.['My rankings']?.href || {}
    // const rankingItems: PanelItem[] = Object.entries(rankingsHref).map(([key, val]: any) => ({
    //   title: key,
    //   href: val.href?.startsWith('http') ? val.href : undefined,
    //   to: val.href && !val.href.startsWith('http') ? `/${val.href}` : undefined,
    //   target: val.href?.startsWith('http') ? '_blank' : undefined,
    //   appendIcon: val.href?.startsWith('http') ? 'mdi-open-in-new' : undefined,
    // }))
    // panels.push({ title: 'My Rankings', items: rankingItems, borderLeft: '8px' })
    
    // More Info Section - Unhide to show More Info
    // const aboutHref = props.memberData?.site_map?.['About']?.href || {}
    // const aboutItems: PanelItem[] = Object.entries(aboutHref).map(([key, val]: any) => ({
    //   title: key,
    //   href: val.href?.startsWith('http') ? val.href : undefined,
    //   to: val.href && !val.href.startsWith('http') ? `/${val.href}` : undefined,
    //   target: val.href?.startsWith('http') ? '_blank' : undefined,
    //   appendIcon: val.href?.startsWith('http') ? 'mdi-open-in-new' : undefined,
    // }))
    // panels.push({ title: 'More Info', items: aboutItems, borderLeft: '8px' })
    
    return panels
})

// NGB item
type NgbResolved = { img: string; text: string; href: string }
const ngbItem = computed<NgbResolved>(() => {
    const s = props.ngb ?? {}
    return {
        img: s.img ?? '/maya/icons/icon-player-white.svg',
        text: (s.text ?? 'No NGB Set').toString(),
        href: s.href ?? '/',
    }
})

// Open panels
const panelIndex = ref<number[]>([0])
watch(panelIndex, (val) => {
    if (!val.includes(0)) val.unshift(0)
    const lastIndex = menuPanels.value.length - 1
    if (!val.includes(lastIndex)) val.push(lastIndex)
})
</script>

<style lang="scss">

.side_menu_skeleton_loader {
    width: 100%;
    height: 64px;
    margin: 0 auto;
    padding: 0;
}

// Styling for the side Menu Panel
.side_menu_panel {
    // top: 50px !important;
    background-color: rgb(var(--v-theme-primary)) !important;
    height: 100% !important;
    overflow: hidden !important;
    ::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE 10+ */
    scrollbar-width: none;    /* Firefox */
    
    // Ensure navigation drawer content is displayed in a column layout
    .v-navigation-drawer__content {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }
    
    // Set the background/border color of the expansion panel text
    .v-expansion-panel-text {
        background: rgb(var(--v-theme-primary));
        border: solid;
        border-color: rgb(var(--v-theme-primary-800));
    }
    
    // Set border on the expansion panel title
    .v-expansion-panel-title {
        border: solid;
        border-width: 1px 0px 0px 0px;
        border-color: rgb(var(--v-theme-primary-800));
    }
    
    // Disable the expansion panel title overlay
    .v-expansion-panel-title__overlay {
        display: none;
    }
    
    // Removes the default padding and margin from the expansion panel
    .v-expansion-panel-text__wrapper {
        padding: 0px;
    }
    
    // Remove overlay from the list items to stop double clicks
    .v-list-item__overlay {
        display: none !important;
    } 
    
    // Styling for the NGB section in the side menu
    .menu_panel_item-ngb {
        opacity: 1 !important;
        
        // Remove border radius from the NGB image
        .v-avatar {
            border-radius: 0px;
        }
        
        // Set size on the NGB image
        .v-avatar.v-avatar--density-comfortable {
            height: 24px;
            width: 24px;
        }
        
        // Add with to align NBG text center
        .v-list-item__append {
            width: 29px;
        }
        
        // Set equal width for NGB items
        .v-list-item__spacer {
            width: 8px;
        }
        
        // Style the NGB title text
        .v-list-item-title {
            text-transform: uppercase !important;
            color: #fff !important;
            font-family: $brandon-grotesque-font;
            font-size: 15px !important;
            font-weight: bold !important;
            text-align: left;
            font-stretch: normal;
            font-style: normal;
            line-height: 1 !important;
            letter-spacing: 2.25px !important;
            display: flex;
            justify-content: center;
            width: 100%;
        }
    }
}

// Style the side menu logo and text
.side_menu_logo {
    padding: 3px 13px !important;
    
    .v-avatar {
        height: 32px;
        width: 32px;
        border-radius: 0px;
    }
}

// Style the side menu individual expansion panels
.side_menu_expansions {
    
    // Style the expansion panels
    justify-content: flex-start !important;
    
    // Set seperate font size for the title of the menu panel
    #menu_panel_title .v-list-item-title {
        font-size: 11px !important;
    }
    
    // Styles for the menu panel items
    .v-list-item-title {
        text-transform: uppercase !important;
        font-family: $brandon-grotesque-font;
        font-size: 13px !important;
        font-weight: bold !important;
        text-align: left;
        font-stretch: normal;
        font-style: normal;
        line-height: 1 !important;
        letter-spacing: 2.25px !important;
        width: 350px;
        white-space: normal;
        word-break: break-word;
        overflow-wrap: break-word;
    }
    
    // Styling for the menu panel item containers
    .v-list-item--nav {
        border: solid;
        border-width: 1px 0px 0px 0px;
        border-color: rgb(var(--v-theme-primary-800));
        border-radius: 0%;
        min-height: 50px !important;
        max-height: 50px !important;
        color: #fff;
        margin-bottom: 0px !important;
        padding: 0px 17px !important;
        border-radius: 0px !important;
    }
    .v-list-item--nav:last-of-type {
        border-bottom: 1px solid rgb(var(--v-theme-primary-800));
    }
    
}

// Styling for the individual menu panel items
.menu_panel_item {
    .v-avatar {
        height: 20px !important;
        width: 20px !important;
        border-radius: 0px;
    }
}

</style>